import { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const DAILY_LIMIT = 35;

const TOP_COUNTRIES = [
  {name:'China',flag:'🇨🇳'},{name:'United States',flag:'🇺🇸'},{name:'India',flag:'🇮🇳'},
  {name:'United Kingdom',flag:'🇬🇧'},{name:'Germany',flag:'🇩🇪'},{name:'Italy',flag:'🇮🇹'},
  {name:'Australia',flag:'🇦🇺'},{name:'Canada',flag:'🇨🇦'},{name:'South Korea',flag:'🇰🇷'},
  {name:'Japan',flag:'🇯🇵'},{name:'Brazil',flag:'🇧🇷'},{name:'Iran',flag:'🇮🇷'},
  {name:'Spain',flag:'🇪🇸'},{name:'Turkey',flag:'🇹🇷'},{name:'France',flag:'🇫🇷'},
  {name:'Indonesia',flag:'🇮🇩'},{name:'Netherlands',flag:'🇳🇱'},{name:'Malaysia',flag:'🇲🇾'},
  {name:'Poland',flag:'🇵🇱'},{name:'Taiwan',flag:'🇹🇼'},{name:'Saudi Arabia',flag:'🇸🇦'},
  {name:'Portugal',flag:'🇵🇹'},{name:'Pakistan',flag:'🇵🇰'},{name:'Sweden',flag:'🇸🇪'},
  {name:'Switzerland',flag:'🇨🇭'},{name:'Egypt',flag:'🇪🇬'},{name:'Mexico',flag:'🇲🇽'},
  {name:'South Africa',flag:'🇿🇦'},{name:'Greece',flag:'🇬🇷'},{name:'Thailand',flag:'🇹🇭'},
  {name:'Belgium',flag:'🇧🇪'},{name:'Argentina',flag:'🇦🇷'},{name:'Russia',flag:'🇷🇺'},
  {name:'Singapore',flag:'🇸🇬'},{name:'Norway',flag:'🇳🇴'},{name:'Denmark',flag:'🇩🇰'},
  {name:'Austria',flag:'🇦🇹'},{name:'Finland',flag:'🇫🇮'},{name:'Romania',flag:'🇷🇴'},
  {name:'Hungary',flag:'🇭🇺'},{name:'Chile',flag:'🇨🇱'},{name:'New Zealand',flag:'🇳🇿'},
  {name:'United Arab Emirates',flag:'🇦🇪'},{name:'Colombia',flag:'🇨🇴'},{name:'Vietnam',flag:'🇻🇳'},
  {name:'Philippines',flag:'🇵🇭'},{name:'Nigeria',flag:'🇳🇬'},{name:'Bangladesh',flag:'🇧🇩'},
  {name:'Iraq',flag:'🇮🇶'},{name:'Morocco',flag:'🇲🇦'},{name:'Hong Kong',flag:'🇭🇰'},
  {name:'Ireland',flag:'🇮🇪'},{name:'Ukraine',flag:'🇺🇦'},
];

const FUNDING_TYPES = [{name:'Fully Funded'},{name:'Partially Funded'}];

const FUNDING_COLORS = {
  'Fully Funded':    {bg:'rgba(5,150,105,0.2)', color:'#34d399', border:'rgba(5,150,105,0.4)'},
  'Partially Funded':{bg:'rgba(217,119,6,0.2)', color:'#fbbf24', border:'rgba(217,119,6,0.4)'},
};
const AV_COLORS = [
  ['#4f46e5','#fff'],['#0891b2','#fff'],['#059669','#fff'],['#d97706','#fff'],
  ['#dc2626','#fff'],['#7c3aed','#fff'],['#db2777','#fff'],['#0284c7','#fff'],
];
const DEPT_COLORS = [
  {bg:'rgba(99,102,241,0.15)', color:'#a5b4fc', border:'rgba(99,102,241,0.3)'},
  {bg:'rgba(16,185,129,0.15)', color:'#6ee7b7', border:'rgba(16,185,129,0.3)'},
  {bg:'rgba(245,158,11,0.15)', color:'#fcd34d', border:'rgba(245,158,11,0.3)'},
  {bg:'rgba(239,68,68,0.15)',  color:'#fca5a5', border:'rgba(239,68,68,0.3)' },
];
const RESEARCH_COLORS = [
  {bg:'rgba(59,130,246,0.2)',  color:'#93c5fd', border:'rgba(59,130,246,0.4)'},
  {bg:'rgba(99,102,241,0.2)',  color:'#a5b4fc', border:'rgba(99,102,241,0.4)'},
  {bg:'rgba(16,185,129,0.2)',  color:'#6ee7b7', border:'rgba(16,185,129,0.4)'},
  {bg:'rgba(236,72,153,0.2)',  color:'#f9a8d4', border:'rgba(236,72,153,0.4)'},
];

function hashStr(str=''){let h=0;for(let i=0;i<str.length;i++)h=(h*31+str.charCodeAt(i))&0xffff;return h;}
function getInitials(name=''){return name.replace(/Dr\.|Prof\./g,'').trim().split(' ').filter(Boolean).slice(0,2).map(n=>n[0]).join('').toUpperCase();}

function useDebounce(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ── Country dropdown ───────────────────────────────────────────
function CountryFilter({selected, onSelect}) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    function handle(e){ if(ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, []);

  const visible     = TOP_COUNTRIES.filter(c => c.name.toLowerCase().includes(query.toLowerCase()));
  const selectedObj = TOP_COUNTRIES.find(c => c.name === selected);

  return (
    <div ref={ref} style={{position:'relative'}}>
      <button onClick={()=>setOpen(o=>!o)}
        style={{width:'100%',display:'flex',alignItems:'center',gap:'8px',padding:'10px 14px',background:open?'rgba(99,102,241,0.12)':'rgba(255,255,255,0.03)',border:`1px solid ${open?'rgba(99,102,241,0.5)':'rgba(255,255,255,0.1)'}`,borderRadius:'10px',cursor:'pointer',textAlign:'center'}}>
        <span style={{fontSize:'15px'}}>🌍</span>
        <span style={{fontSize:'13px',fontWeight:'600',color:open?'#c4b5fd':'#94a3b8'}}>Country</span>
        <span style={{flex:1,fontSize:'12px',color:selected?'#f1f5f9':'#475569',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',textAlign:'right'}}>
          {selected ? `${selectedObj?.flag||''} ${selected}` : 'All'}
        </span>
        {selected && <span onClick={e=>{e.stopPropagation();onSelect('');}} style={{color:'#ef4444',fontSize:'14px'}}>✕</span>}
        <span style={{fontSize:'10px',color:'#475569',transform:open?'rotate(180deg)':'none'}}>▼</span>
      </button>
      {open && (
        <div style={{position:'absolute',top:'calc(100% + 4px)',left:0,right:0,background:'#0d1526',border:'1px solid rgba(99,102,241,0.3)',borderRadius:'12px',zIndex:200,boxShadow:'0 8px 32px rgba(0,0,0,0.5)'}}>
          <div style={{padding:'8px 10px',borderBottom:'1px solid rgba(255,255,255,0.06)'}}>
            <input autoFocus value={query} onChange={e=>setQuery(e.target.value)} placeholder="Search country..."
              style={{width:'100%',padding:'7px 10px',background:'rgba(255,255,255,0.08)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'8px',color:'#e2e8f0',fontSize:'12px',outline:'none',boxSizing:'border-box'}}/>
          </div>
          <div style={{maxHeight:'260px',overflowY:'auto'}}>
            <div onClick={()=>{onSelect('');setOpen(false);setQuery('');}}
              style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 14px',cursor:'pointer',background:!selected?'rgba(99,102,241,0.15)':'transparent'}}>
              <span>🌍</span><span style={{fontSize:'12px',color:!selected?'#a5b4fc':'#94a3b8',fontWeight:!selected?'600':'400'}}>All Countries</span>
            </div>
            {visible.map(c=>(
              <div key={c.name} onClick={()=>{onSelect(c.name);setOpen(false);setQuery('');}}
                style={{display:'flex',alignItems:'center',gap:'10px',padding:'9px 14px',cursor:'pointer',background:selected===c.name?'rgba(99,102,241,0.15)':'transparent'}}>
                <span style={{fontSize:'15px'}}>{c.flag}</span>
                <span style={{fontSize:'12px',color:selected===c.name?'#a5b4fc':'#94a3b8',fontWeight:selected===c.name?'600':'400'}}>{c.name}</span>
              </div>
            ))}
            {visible.length===0 && <div style={{padding:'14px',textAlign:'center',fontSize:'12px',color:'#475569'}}>No results</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function FundingFilter({selected, onSelect}) {
  return (
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px',padding:'0 2px'}}>
        <span style={{fontSize:'14px'}}>💰</span>
        <span style={{fontSize:'12px',fontWeight:'600',color:'#64748b'}}>Funding</span>
        {selected && <span onClick={()=>onSelect('')} style={{marginLeft:'auto',fontSize:'10px',color:'#ef4444',cursor:'pointer',fontWeight:'600'}}>Clear</span>}
      </div>
      <div style={{display:'flex',flexDirection:'column',gap:'5px'}}>
        {FUNDING_TYPES.map(f=>{
          const active = selected === f.name;
          const color  = f.name==='Fully Funded'?'#34d399':'#fbbf24';
          const bg     = f.name==='Fully Funded'?'rgba(5,150,105,0.15)':'rgba(217,119,6,0.15)';
          return(
            <button key={f.name} onClick={()=>onSelect(active?'':f.name)}
              style={{display:'flex',alignItems:'center',gap:'8px',padding:'9px 12px',background:active?bg:'rgba(255,255,255,0.03)',border:`1px solid ${active?color:'rgba(255,255,255,0.08)'}`,borderRadius:'9px',cursor:'pointer'}}>
              <div style={{width:'8px',height:'8px',borderRadius:'50%',background:color}}/>
              <span style={{fontSize:'12px',fontWeight:active?'700':'400',color:active?color:'#94a3b8'}}>{f.name}</span>
              {active && <span style={{marginLeft:'auto',color,fontSize:'11px'}}>✓</span>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Free-text filter (Research Area — free, University/Department — Pro) ──
function TextFilter({icon, label, placeholder, value, onChange, locked, onUpgradeClick}) {
  if (locked) {
    return (
      <div onClick={onUpgradeClick} style={{cursor:'pointer'}} title="Upgrade to Pro">
        <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px',padding:'0 2px'}}>
          <span style={{fontSize:'14px'}}>{icon}</span>
          <span style={{fontSize:'12px',fontWeight:'600',color:'#475569'}}>{label}</span>
          <span style={{marginLeft:'auto',background:'rgba(251,191,36,0.12)',border:'1px solid rgba(251,191,36,0.3)',borderRadius:'20px',padding:'1px 7px',fontSize:'9px',fontWeight:'700',color:'#fbbf24'}}>🔒 PRO</span>
        </div>
        <div style={{position:'relative',opacity:0.5}}>
          <input readOnly placeholder={placeholder} onClick={onUpgradeClick}
            style={{width:'100%',padding:'9px 10px',background:'rgba(255,255,255,0.02)',border:'1px solid rgba(255,255,255,0.07)',borderRadius:'9px',color:'#475569',fontSize:'12px',cursor:'pointer',boxSizing:'border-box'}}/>
        </div>
      </div>
    );
  }
  return(
    <div>
      <div style={{display:'flex',alignItems:'center',gap:'8px',marginBottom:'6px',padding:'0 2px'}}>
        <span style={{fontSize:'14px'}}>{icon}</span>
        <span style={{fontSize:'12px',fontWeight:'600',color:'#64748b'}}>{label}</span>
        {value && <span onClick={()=>onChange('')} style={{marginLeft:'auto',fontSize:'10px',color:'#ef4444',cursor:'pointer',fontWeight:'600'}}>Clear</span>}
      </div>
      <div style={{position:'relative'}}>
        <span style={{position:'absolute',left:'10px',top:'50%',transform:'translateY(-50%)',fontSize:'12px',color:'#475569',pointerEvents:'none'}}>🔍</span>
        <input
          value={value}
          onChange={e=>onChange(e.target.value)}
          placeholder={placeholder}
          style={{width:'100%',padding:'9px 10px 9px 28px',background:value?'rgba(99,102,241,0.08)':'rgba(255,255,255,0.04)',border:`1px solid ${value?'rgba(99,102,241,0.4)':'rgba(255,255,255,0.1)'}`,borderRadius:'9px',color:'#e2e8f0',fontSize:'12px',outline:'none',boxSizing:'border-box'}}
        />
      </div>
    </div>
  );
}

// ── Professor Row ─────────────────────────────────────────────
function ProfRow({prof, onContact, isRevealed}) {
  const [avBg, avTx] = AV_COLORS[hashStr(prof.name||'') % AV_COLORS.length];
  const deptStyle    = DEPT_COLORS[hashStr(prof.department||'') % DEPT_COLORS.length];
  const resStyle     = RESEARCH_COLORS[hashStr(prof.research_area||'') % RESEARCH_COLORS.length];
  const fundStyle    = FUNDING_COLORS[prof.funding] || FUNDING_COLORS['Partially Funded'];
  const countryObj   = TOP_COUNTRIES.find(c => c.name === prof.country);
  const td           = {padding:'10px 12px', verticalAlign:'middle'};

  return (
  <tr style={{borderBottom:'1px solid rgba(255,255,255,0.04)',transition:'background 0.12s'}}
    onMouseEnter={e=>e.currentTarget.style.background='rgba(99,102,241,0.06)'}
    onMouseLeave={e=>e.currentTarget.style.background='transparent'}>

    {/* NAME — left-aligned, NO change */}
    <td style={{...td,width:'190px'}}>
      <div style={{display:'flex',alignItems:'center',gap:'8px'}}>
        <div style={{width:'32px',height:'32px',borderRadius:'50%',background:avBg,color:avTx,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'11px',fontWeight:'700',flexShrink:0}}>
          {getInitials(prof.name)}
        </div>
        <span title={prof.name} style={{fontSize:'13px',fontWeight:'600',color:'#f1f5f9',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:'1.35',maxWidth:'145px'}}>
          {prof.name||'—'}
        </span>
      </div>
    </td>

    {/* UNIVERSITY — centered */}
    <td style={{...td,width:'190px',textAlign:'center'}}>
      <span title={prof.university} style={{fontSize:'12px',color:'#94a3b8',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:'1.45',maxWidth:'180px',wordBreak:'break-word',margin:'0 auto'}}>
        {prof.university||'—'}
      </span>
    </td>

    {/* DEPARTMENT — centered, capped width + ellipsis (no more overlap) */}
    <td style={{...td,width:'200px',textAlign:'center'}}>
      <span title={prof.department} style={{
        fontSize:'11px',fontWeight:'500',
        background:deptStyle.bg,color:deptStyle.color,
        border:`1px solid ${deptStyle.border}`,
        padding:'3px 8px',borderRadius:'20px',
        display:'inline-block',maxWidth:'190px',
        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
      }}>
        {prof.department||'—'}
      </span>
    </td>

    {/* RESEARCH AREA — centered, capped width + ellipsis (no more overlap) */}
    <td style={{...td,width:'240px',textAlign:'center'}}>
      <span title={prof.research_area} style={{
        fontSize:'11px',fontWeight:'500',
        background:resStyle.bg,color:resStyle.color,
        border:`1px solid ${resStyle.border}`,
        padding:'3px 8px',borderRadius:'20px',
        display:'inline-block',maxWidth:'230px',
        overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap',
      }}>
        {prof.research_area||'—'}
      </span>
    </td>

    {/* COUNTRY — already centered, no change */}
    <td style={{...td,width:'110px',textAlign:'center'}}>
      <div style={{display:'flex',flexDirection:'column',alignItems:'center',gap:'3px'}}>
        <span style={{fontSize:'18px',lineHeight:1}}>{countryObj?.flag||'🌍'}</span>
        <span style={{fontSize:'10px',color:'#94a3b8',textAlign:'center',display:'-webkit-box',WebkitLineClamp:2,WebkitBoxOrient:'vertical',overflow:'hidden',lineHeight:'1.3',maxWidth:'100px',wordBreak:'break-word'}}>
          {prof.country||'—'}
        </span>
      </div>
    </td>

    {/* FUNDING — already centered, no change */}
    <td style={{...td,width:'130px',textAlign:'center'}}>
      <span style={{fontSize:'11px',fontWeight:'500',background:fundStyle.bg,color:fundStyle.color,border:`1px solid ${fundStyle.border}`,padding:'3px 9px',borderRadius:'20px',display:'inline-block',whiteSpace:'nowrap'}}>
        {prof.funding}
      </span>
    </td>

    {/* CONTACT — already centered, no change */}
    <td style={{...td,width:'120px',textAlign:'center'}}>
      {isRevealed ? (
        <button onClick={()=>onContact(prof)}
          style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'6px 10px',background:'rgba(16,185,129,0.12)',border:'1px solid rgba(16,185,129,0.35)',borderRadius:'8px',color:'#10b981',fontSize:'11px',fontWeight:'600',cursor:'pointer',whiteSpace:'nowrap'}}>
          ✓ Email Seen
        </button>
      ) : (
        <button onClick={()=>onContact(prof)}
          style={{display:'inline-flex',alignItems:'center',gap:'4px',padding:'6px 12px',background:'#6366f1',border:'none',borderRadius:'8px',color:'#fff',fontSize:'12px',fontWeight:'600',cursor:'pointer',whiteSpace:'nowrap'}}>
          ✉ Contact
        </button>
      )}
    </td>
  </tr>
);
}

// ── Main ──────────────────────────────────────────────────────
export default function ProfessorPage({requireLogin, requireUpgrade}) {
  const {user, revealEmail} = useAuth();

  // ✅ Pro/Premium plan check
  const isPro = !!(user && (user.plan === 'pro' || user.plan === 'premium'));

  const [professors,    setProfessors]    = useState([]);
  const [total,         setTotal]         = useState(0);
  const [page,          setPage]          = useState(1);
  const [loading,       setLoading]       = useState(false);
  const [revealed,      setRevealed]      = useState([]);
  const [contactProf,   setContactProf]   = useState(null);
  const [revealedEmail, setRevealedEmail] = useState(null);
  const [revealLoading, setRevealLoading] = useState(false);

  const [selectedCountry,  setSelectedCountry]  = useState('');
  const [selectedFunding,  setSelectedFunding]  = useState('');
  const [researchInput,    setResearchInput]    = useState('');
  const [universityInput,  setUniversityInput]  = useState('');
  const [departmentInput,  setDepartmentInput]  = useState('');

  const debouncedCountry    = useDebounce(selectedCountry,  200);
  const debouncedResearch   = useDebounce(researchInput,    500);
  const debouncedUniversity = useDebounce(universityInput,  500);
  const debouncedDepartment = useDebounce(departmentInput,  500);

  const isFiltered = !!(selectedCountry || selectedFunding || researchInput ||
    (isPro && (universityInput || departmentInput)));

  const LIMIT      = 15;
  const COL_WIDTHS = [190, 190, 200, 240, 110, 130, 120]; // wider dept + research

  // ✅ Abort controller — prevents stale/overlapping requests
  const abortRef = useRef(null);

  const fetchProfessors = useCallback(async (pg = 1) => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    try {
      const params = {page: pg, limit: LIMIT};
      if (debouncedCountry)  params.country       = debouncedCountry;
      if (selectedFunding)   params.funding       = selectedFunding;
      if (debouncedResearch) params.research_area = debouncedResearch;
      if (isPro && debouncedUniversity) params.university = debouncedUniversity;
      if (isPro && debouncedDepartment) params.department = debouncedDepartment;

      const res = await axios.get(`${API}/professors`, {params, signal: controller.signal});
      setProfessors(res.data.professors || []);
      setTotal(res.data.total || 0);
      setPage(pg);
    } catch (err) {
      if (axios.isCancel(err) || err.name === 'CanceledError') return; // ignore aborted
      toast.error('Could not load professors');
    }
    setLoading(false);
  }, [debouncedCountry, selectedFunding, debouncedResearch, debouncedUniversity, debouncedDepartment, isPro]);

  useEffect(() => { fetchProfessors(1); }, [fetchProfessors]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/auth/revealed-professors`)
      .then(res => setRevealed(res.data.professorIds || []))
      .catch(() => {});
  }, [user]);

  const clearAll = () => {
    setSelectedCountry(''); setSelectedFunding(''); setResearchInput('');
    setUniversityInput(''); setDepartmentInput('');
  };

  const freeActive  = [selectedCountry, selectedFunding, researchInput].filter(Boolean).length;
  const proActive   = isPro ? [universityInput, departmentInput].filter(Boolean).length : 0;
  const totalActive = freeActive + proActive;
  const totalPages  = Math.ceil(total / LIMIT);

  const statProfs     = isFiltered ? (total > 0 ? total.toLocaleString() : '0') : '500K+';
  const statCountries = isFiltered ? (selectedCountry ? '1' : '150+') : '150+';
  const statResearch  = isFiltered ? (researchInput ? 'Filtered' : '400+') : '400+';

  const handleContact = (prof) => {
    if (!user) { requireLogin && requireLogin(); return; }
    if (user.credits <= 0) { requireUpgrade && requireUpgrade(); return; }
    if ((user.creditsUsedToday || 0) >= DAILY_LIMIT) {
      toast.error(`Daily limit: ${DAILY_LIMIT} emails/day.`); return;
    }
    setRevealedEmail(null);
    setContactProf(prof);
  };

  const handleReveal = async () => {
    if (!user) { requireLogin && requireLogin(); return; }
    if (user.credits <= 0) {
      toast.error('No credits! Please upgrade.');
      requireUpgrade && requireUpgrade();
      setContactProf(null);
      return;
    }
    if ((user.creditsUsedToday || 0) >= DAILY_LIMIT) {
      toast.error(`Daily limit of ${DAILY_LIMIT} reached!`);
      setContactProf(null);
      return;
    }
    setRevealLoading(true);
    try {
      const data = await revealEmail(contactProf._id);
      setRevealedEmail(data.email);
      setRevealed(prev => [...new Set([...prev, contactProf._id?.toString()])]);
      toast.success(data.alreadyRevealed ? '✉️ Email revealed!' : `✉️ Email revealed! ${data.creditsLeft} credits left`);
    } catch (err) {
      const msg = err.response?.data?.message;
      if (msg === 'daily_limit')     toast.error('Daily limit reached!');
      else if (msg === 'no_credits') { toast.error('No credits!'); requireUpgrade && requireUpgrade(); }
      else                           toast.error('Could not reveal email. Try again.');
      setContactProf(null);
    }
    setRevealLoading(false);
  };

  return (
    <div style={{display:'flex',minHeight:'calc(100vh - 68px)',background:'#060b14',fontFamily:'Inter,sans-serif'}}>

      {/* ── SIDEBAR ── */}
      <div style={{width:'248px',flexShrink:0,background:'#080f1a',borderRight:'1px solid rgba(255,255,255,0.07)',position:'sticky',top:'68px',height:'calc(100vh - 68px)',overflowY:'auto',display:'flex',flexDirection:'column'}}>

        <div style={{padding:'14px 14px 10px',borderBottom:'1px solid rgba(255,255,255,0.06)',flexShrink:0}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <span style={{fontSize:'10px',fontWeight:'700',color:'#475569',textTransform:'uppercase',letterSpacing:'1.2px'}}>FILTERS</span>
            {totalActive > 0 && (
              <button onClick={clearAll} style={{fontSize:'11px',color:'#ef4444',background:'transparent',border:'none',cursor:'pointer',fontWeight:'600'}}>
                Clear ({totalActive})
              </button>
            )}
          </div>
        </div>

        <div style={{padding:'12px 14px 6px',flexShrink:0}}>
          <span style={{fontSize:'9px',fontWeight:'700',color:'#22c55e',textTransform:'uppercase',letterSpacing:'1px'}}>✓ Free Filters</span>
        </div>

        <div style={{padding:'0 12px',display:'flex',flexDirection:'column',gap:'10px',flexShrink:0}}>
          <CountryFilter selected={selectedCountry} onSelect={setSelectedCountry}/>
          <FundingFilter selected={selectedFunding} onSelect={setSelectedFunding}/>
          <TextFilter icon="🔬" label="Research Area" placeholder="e.g. Machine Learning, AI..." value={researchInput} onChange={setResearchInput}/>
        </div>

        <div style={{margin:'14px 12px 8px',display:'flex',alignItems:'center',gap:'8px'}}>
          <div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}/>
          <span style={{fontSize:'9px',fontWeight:'700',color:'#fbbf24',textTransform:'uppercase',letterSpacing:'1px',whiteSpace:'nowrap'}}>
            {isPro ? '⭐ Pro Filters' : '🔒 Pro Filters'}
          </span>
          <div style={{flex:1,height:'1px',background:'rgba(255,255,255,0.06)'}}/>
        </div>

        {/* ✅ Real, working filters for Pro users; locked upsell for free users */}
        <div style={{padding:'0 12px',display:'flex',flexDirection:'column',gap:'10px',flexShrink:0}}>
          <TextFilter icon="🎓" label="University" placeholder="e.g. MIT, Oxford..."
            value={universityInput} onChange={setUniversityInput}
            locked={!isPro} onUpgradeClick={()=>requireUpgrade&&requireUpgrade()}/>
          <TextFilter icon="🏛️" label="Department" placeholder="e.g. Computer Science..."
            value={departmentInput} onChange={setDepartmentInput}
            locked={!isPro} onUpgradeClick={()=>requireUpgrade&&requireUpgrade()}/>
        </div>

        {!isPro && (
          <div style={{margin:'14px 12px',padding:'12px 14px',background:'rgba(99,102,241,0.08)',border:'1px solid rgba(251,191,36,0.2)',borderRadius:'10px',cursor:'pointer',flexShrink:0}}
            onClick={()=>requireUpgrade&&requireUpgrade()}>
            <div style={{fontSize:'12px',fontWeight:'700',color:'#fbbf24',marginBottom:'4px'}}>⚡ Upgrade to Pro</div>
            <div style={{fontSize:'11px',color:'#64748b',lineHeight:1.4,marginBottom:'8px'}}>Search by specific university or department</div>
            <div style={{padding:'6px',background:'#6366f1',borderRadius:'7px',textAlign:'center',fontSize:'11px',fontWeight:'700',color:'#fff'}}>Upgrade Now →</div>
          </div>
        )}

        {totalActive > 0 && (
          <div style={{padding:'0 12px 12px',display:'flex',flexWrap:'wrap',gap:'5px',flexShrink:0}}>
            {[
              selectedCountry && {key:'c', label:selectedCountry, clear:()=>setSelectedCountry('')},
              selectedFunding && {key:'f', label:selectedFunding, clear:()=>setSelectedFunding('')},
              researchInput   && {key:'ra',label:`Research: ${researchInput}`, clear:()=>setResearchInput('')},
              isPro && universityInput && {key:'u', label:`Uni: ${universityInput}`, clear:()=>setUniversityInput('')},
              isPro && departmentInput && {key:'d', label:`Dept: ${departmentInput}`, clear:()=>setDepartmentInput('')},
            ].filter(Boolean).map(tag=>(
              <span key={tag.key} onClick={tag.clear}
                style={{display:'inline-flex',alignItems:'center',gap:'4px',fontSize:'10px',background:'rgba(99,102,241,0.15)',color:'#a5b4fc',border:'1px solid rgba(99,102,241,0.35)',padding:'3px 9px',borderRadius:'20px',cursor:'pointer',maxWidth:'200px',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {tag.label} ✕
              </span>
            ))}
          </div>
        )}
      </div>

      {/* ── MAIN ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden',minWidth:0}}>

        <div style={{padding:'12px 20px',borderBottom:'1px solid rgba(255,255,255,0.06)',background:'#080f1a',flexShrink:0,display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px'}}>
          <p style={{margin:0,fontSize:'12px',color:'#94a3b8',lineHeight:1.5}}>
            🔍 <strong style={{color:'#f1f5f9'}}>Find PhD supervisors:</strong>
            {' '}<span style={{color:'#818cf8'}}>Select country</span>
            <span style={{color:'#334155'}}> → </span>
            <span style={{color:'#818cf8'}}>Type research area</span>
            <span style={{color:'#334155'}}> → </span>
            <span style={{color:'#10b981',fontWeight:'500'}}>Contact professor</span>
          </p>
          <div style={{display:'flex',alignItems:'center',gap:'8px',flexShrink:0}}>
            {user && (
              <div style={{fontSize:'11px',padding:'4px 10px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.03)',whiteSpace:'nowrap',color:'#64748b'}}>
                Today: <strong style={{color:(user.creditsUsedToday||0)>=DAILY_LIMIT?'#ef4444':'#10b981'}}>
                  {user.creditsUsedToday||0}/{DAILY_LIMIT}
                </strong>
              </div>
            )}
            <div style={{fontSize:'12px',color:'#475569',padding:'5px 12px',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.08)',background:'rgba(255,255,255,0.03)',whiteSpace:'nowrap'}}>
              <strong style={{color:'#a5b4fc'}}>{statProfs}</strong> professors
            </div>
          </div>
        </div>

        <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:'10px',padding:'12px 16px',borderBottom:'1px solid rgba(255,255,255,0.06)',background:'#080f1a',flexShrink:0}}>
          {[
            {val:statProfs,         lbl:isFiltered?'Filtered':'Total Professors', icon:'👨‍🏫', color:'#818cf8'},
            {val:statCountries,     lbl:'Countries',                               icon:'🌍', color:'#34d399'},
            {val:statResearch,      lbl:'Research Areas',                          icon:'🔬', color:'#60a5fa'},
            {val:professors.length, lbl:'Showing Now',                             icon:'📋', color:'#fbbf24'},
          ].map((s,i)=>(
            <div key={i} style={{textAlign:'center',padding:'12px 6px',background:'rgba(255,255,255,0.03)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'10px',display:'flex',flexDirection:'column',alignItems:'center',gap:'4px'}}>
              <span style={{fontSize:'18px'}}>{s.icon}</span>
              <div style={{fontSize:'18px',fontWeight:'700',color:s.color,lineHeight:1}}>{s.val}</div>
              <div style={{fontSize:'10px',color:'#475569'}}>{s.lbl}</div>
            </div>
          ))}
        </div>

        <div style={{overflowX:'auto',overflowY:'auto',flex:1}}>
          <table style={{borderCollapse:'collapse',tableLayout:'fixed',width:`${COL_WIDTHS.reduce((a,b)=>a+b,0)}px`}}>
            <thead style={{position:'sticky',top:0,zIndex:2,background:'#0d1526'}}>
              <tr style={{borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
                {[
                  {label:'NAME',          w:COL_WIDTHS[0]},
                  {label:'UNIVERSITY',    w:COL_WIDTHS[1]},
                  {label:'DEPARTMENT',    w:COL_WIDTHS[2]},
                  {label:'RESEARCH AREA', w:COL_WIDTHS[3]},
                  {label:'COUNTRY',       w:COL_WIDTHS[4]},
                  {label:'FUNDING',       w:COL_WIDTHS[5]},
                  {label:'CONTACT',       w:COL_WIDTHS[6]},
                ].map((h,i)=>(
                  <th key={i} style={{width:`${h.w}px`,padding:'9px 12px',textAlign:'center',fontSize:'10px',fontWeight:'700',color:'#334155',textTransform:'uppercase',letterSpacing:'0.8px',whiteSpace:'nowrap'}}>
                    {h.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && Array.from({length:10}).map((_,i)=>(
                <tr key={i} style={{borderBottom:'1px solid rgba(255,255,255,0.04)'}}>
                  {COL_WIDTHS.map((w,j)=>(
                    <td key={j} style={{padding:'10px 12px',width:`${w}px`}}>
                      <div style={{height:'11px',borderRadius:'4px',background:'rgba(255,255,255,0.05)',width:'70%'}}/>
                    </td>
                  ))}
                </tr>
              ))}

              {!loading && professors.map(prof=>(
                <ProfRow key={prof._id} prof={prof} onContact={handleContact}
                  isRevealed={revealed.includes(prof._id?.toString())}/>
              ))}

              {!loading && professors.length === 0 && (
                <tr>
                  <td colSpan={7} style={{textAlign:'center',padding:'80px 0'}}>
                    <div style={{fontSize:'44px',marginBottom:'12px'}}>🔍</div>
                    <div style={{fontSize:'15px',color:'#64748b',marginBottom:'6px'}}>No professors found</div>
                    <div style={{fontSize:'12px',color:'#334155',marginBottom:'16px'}}>Try a different keyword or clear filters</div>
                    <button onClick={clearAll}
                      style={{padding:'8px 20px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',borderRadius:'8px',color:'#818cf8',fontSize:'13px',cursor:'pointer'}}>
                      Clear all filters
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',padding:'10px 20px',borderTop:'1px solid rgba(255,255,255,0.06)',background:'#080f1a',flexShrink:0}}>
            <span style={{fontSize:'11px',color:'#334155'}}>
              Showing {((page-1)*LIMIT+1).toLocaleString()}–{Math.min(page*LIMIT,total).toLocaleString()} of {total.toLocaleString()}
            </span>
            <div style={{display:'flex',gap:'4px'}}>
              <button onClick={()=>fetchProfessors(page-1)} disabled={page===1}
                style={{padding:'5px 12px',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'6px',color:page===1?'#334155':'#94a3b8',cursor:page===1?'not-allowed':'pointer',fontSize:'12px',opacity:page===1?0.4:1}}>
                ← Prev
              </button>
              {Array.from({length:Math.min(5,totalPages)},(_,i)=>{
                const pg=page<=3?i+1:page-2+i;
                if(pg<1||pg>totalPages)return null;
                return(
                  <button key={pg} onClick={()=>fetchProfessors(pg)}
                    style={{padding:'5px 11px',background:pg===page?'#6366f1':'rgba(255,255,255,0.04)',border:`1px solid ${pg===page?'#6366f1':'rgba(255,255,255,0.08)'}`,borderRadius:'6px',color:pg===page?'#fff':'#94a3b8',cursor:'pointer',fontSize:'12px',fontWeight:pg===page?'700':'400'}}>
                    {pg}
                  </button>
                );
              })}
              <button onClick={()=>fetchProfessors(page+1)} disabled={page===totalPages}
                style={{padding:'5px 12px',background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:'6px',color:page===totalPages?'#334155':'#94a3b8',cursor:page===totalPages?'not-allowed':'pointer',fontSize:'12px',opacity:page===totalPages?0.4:1}}>
                Next →
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── CONTACT MODAL ── */}
      {contactProf && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.8)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:999,padding:'20px'}}
          onClick={()=>{setContactProf(null);setRevealedEmail(null);}}>
          <div style={{background:'#111827',borderRadius:'18px',padding:'32px',width:'100%',maxWidth:'500px',border:'1px solid rgba(255,255,255,0.1)'}}
            onClick={e=>e.stopPropagation()}>

            <h3 style={{fontSize:'18px',fontWeight:'700',color:'#f1f5f9',marginBottom:'6px'}}>
              {revealedEmail?'✉️ Email Revealed!':revealed.includes(contactProf._id?.toString())?'✉️ View Email Again':'Contact Professor'}
            </h3>
            <p style={{fontSize:'13px',color:'#64748b',marginBottom:'20px'}}>
              {revealedEmail?'Copy the email and send your message':revealed.includes(contactProf._id?.toString())?'Already revealed — no credit deducted':'Use 1 credit to reveal professor email address'}
            </p>

            <div style={{background:'rgba(99,102,241,0.08)',borderRadius:'12px',padding:'14px',marginBottom:'16px',border:'1px solid rgba(99,102,241,0.2)'}}>
              <div style={{fontSize:'14px',fontWeight:'600',color:'#f1f5f9',marginBottom:'4px'}}>{contactProf.name}</div>
              <div style={{fontSize:'12px',color:'#64748b'}}>{contactProf.department} · {contactProf.university}</div>
              <div style={{fontSize:'12px',color:'#a5b4fc',marginTop:'4px'}}>{contactProf.research_area}</div>
              <div style={{fontSize:'12px',color:'#94a3b8',marginTop:'2px'}}>{contactProf.country}</div>
            </div>

            {revealedEmail && (
              <div style={{background:'rgba(16,185,129,0.08)',borderRadius:'12px',padding:'16px',marginBottom:'16px',border:'1px solid rgba(16,185,129,0.2)'}}>
                <p style={{fontSize:'11px',color:'#64748b',margin:'0 0 8px',textTransform:'uppercase',letterSpacing:'0.5px'}}>Email Address</p>
                <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',gap:'10px'}}>
                  <span style={{fontSize:'14px',fontWeight:'600',color:'#10b981',wordBreak:'break-all'}}>{revealedEmail}</span>
                  <button onClick={()=>{navigator.clipboard.writeText(revealedEmail);toast.success('Copied!');}}
                    style={{padding:'6px 14px',background:'rgba(16,185,129,0.15)',border:'1px solid rgba(16,185,129,0.3)',borderRadius:'8px',color:'#10b981',fontSize:'12px',fontWeight:'600',cursor:'pointer',flexShrink:0}}>
                    Copy
                  </button>
                </div>
              </div>
            )}

            {!revealedEmail && user && (
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:'16px',fontSize:'12px',color:'#475569',padding:'10px 14px',background:'rgba(255,255,255,0.03)',borderRadius:'8px',border:'1px solid rgba(255,255,255,0.06)'}}>
                <span>Credits: <strong style={{color:'#818cf8'}}>{user.credits}</strong></span>
                <span>Today: <strong style={{color:(user.creditsUsedToday||0)>=DAILY_LIMIT?'#ef4444':'#10b981'}}>
                  {user.creditsUsedToday||0}/{DAILY_LIMIT}
                </strong></span>
              </div>
            )}

            <div style={{display:'flex',gap:'10px'}}>
              {!revealedEmail ? (
                <button onClick={handleReveal} disabled={revealLoading}
                  style={{flex:1,padding:'13px',background:revealLoading?'#4b4f8f':revealed.includes(contactProf._id?.toString())?'#10b981':'#6366f1',border:'none',borderRadius:'10px',color:'#fff',fontSize:'14px',fontWeight:'600',cursor:revealLoading?'not-allowed':'pointer'}}>
                  {revealLoading?'⏳ Revealing...':revealed.includes(contactProf._id?.toString())?'✓ Show Email Again (Free)':'⚡ Reveal Email (1 credit)'}
                </button>
              ) : (
                <a href={`mailto:${revealedEmail}`}
                  style={{flex:1,padding:'13px',background:'#10b981',borderRadius:'10px',color:'#fff',fontSize:'14px',fontWeight:'700',textAlign:'center',textDecoration:'none',display:'block'}}>
                  ✉️ Open in Email App
                </a>
              )}
              <button onClick={()=>{setContactProf(null);setRevealedEmail(null);}}
                style={{padding:'13px 20px',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:'10px',color:'#64748b',fontSize:'14px',cursor:'pointer'}}>
                {revealedEmail?'Done':'Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}